import PropTypes from "prop-types"
import React from "react"

import useWindowSize from "../../../hooks/useWindowSize"
import { healmeLogoBig, starsRating, trustpilotLogo } from "../assets"
import DemoForm from "../components/DemoForm"

const DEFAULT_BULLETS = [
  "Convert new clients with a custom website",
  "Integrated booking and payments",
  "Collect client reviews automatically",
  "1-click booking with client mobile app",
  "Get discovered on the #1 holistic practitioner marketplace"
]

const legacyTitleToSegments = (obj) => {
  const segments = []
  if (obj.before) segments.push({ text: obj.before })
  if (obj.highlight) segments.push({ text: obj.highlight, highlight: true })
  if (obj.after) segments.push({ text: obj.after })
  return segments
}

const DEFAULT_TITLE_SEGMENTS = [
  { text: "Grow & streamline " },
  { text: "your wellness", highlight: true },
  { text: " practice" }
]

const HeroSection = ({
  title,
  titleFontSize,
  titleMobileFontSize,
  titleWidth,
  subtitle,
  heroBullets,
  formTitle,
  formButtonText,
  checkboxText,
  showDemoFormSubtitle,
  demoFormSubtitleLine1,
  demoFormSubtitleLine2,
  fireDemoScheduledEvent
}) => {
  const titleSegments = !title ? DEFAULT_TITLE_SEGMENTS : Array.isArray(title) ? title : legacyTitleToSegments(title)

  const bullets = heroBullets || DEFAULT_BULLETS
  const hasSubtitle = subtitle && subtitle.trim() !== ""
  const hasLongHighlight = titleSegments.some((s) => s.highlight && s.text.length > 6)
  const isMobile = useWindowSize().isMobile

  // On mobile: +2px to title size without adding a line (slight letter-spacing compensates)
  const mobileTitleFontSize = React.useMemo(() => {
    if (!isMobile) return null
    const raw = titleMobileFontSize || "1.108rem"
    const s = String(raw).trim()
    const remMatch = s.match(/^([\d.]+)rem$/)
    const pxMatch = s.match(/^([\d.]+)px$/)
    const rootPx = 16
    const px = remMatch ? parseFloat(remMatch[1]) * rootPx : pxMatch ? parseFloat(pxMatch[1]) : parseFloat(s) || 28
    return `${px + 2}px`
  }, [isMobile, titleMobileFontSize])

  return (
    <section className="pro-hero">
      <div className="pro-hero-logo">
        <img src={healmeLogoBig} alt="Heal.me Logo" width="97" height="32" />
      </div>
      <div className="pro-hero-content">
        <div className={`pro-hero-left ${hasSubtitle ? "pro-hero-left-with-subtitle" : "pro-hero-left-no-subtitle"}`}>
          <h1
            className={`pro-hero-title ${hasLongHighlight ? "pro-hero-title-break-lines" : "pro-hero-title-no-break"}`}
            style={{
              fontSize: isMobile ? mobileTitleFontSize ?? (titleMobileFontSize || "1.108rem") : titleFontSize || "3rem",
              ...(isMobile && mobileTitleFontSize && { letterSpacing: "-0.02em" }),
              ...(titleWidth && { width: titleWidth })
            }}>
            {titleSegments.map((seg, i) => (
              <React.Fragment key={i}>
                <span className={seg.highlight ? "highlight" : undefined}>{seg.text}</span>
                {seg.break && <br />}
              </React.Fragment>
            ))}
          </h1>

          {subtitle && <p className="pro-hero-subtitle">{subtitle}</p>}

          <div className="pro-hero-bullets">
            {bullets.map((text, idx) => (
              <div className="pro-bullet" key={idx}>
                <div className="pro-bullet-icon"></div>
                <span className="pro-bullet-text">{text}</span>
              </div>
            ))}
          </div>

          <div className="pro-trustpilot-badge">
            <span className="pro-trustpilot-text">Excellent</span>
            <img src={starsRating} alt="5 Stars" width="100" height="18" />
            <img src={trustpilotLogo} alt="Trustpilot" width="79" height="22" />
          </div>
        </div>

        <div className="pro-hero-gap" aria-hidden="true" />

        <DemoForm
          showSubtitle={showDemoFormSubtitle}
          subtitleLine1={demoFormSubtitleLine1}
          subtitleLine2={demoFormSubtitleLine2}
          fireDemoScheduledEvent={fireDemoScheduledEvent}
          formTitle={formTitle}
          formButtonText={formButtonText}
          checkboxText={checkboxText}
        />
      </div>
    </section>
  )
}

HeroSection.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.shape({
      before: PropTypes.string,
      highlight: PropTypes.string,
      after: PropTypes.string
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        highlight: PropTypes.bool,
        break: PropTypes.bool
      })
    )
  ]),
  titleFontSize: PropTypes.string,
  titleMobileFontSize: PropTypes.string,
  titleWidth: PropTypes.string,
  subtitle: PropTypes.string,
  heroBullets: PropTypes.arrayOf(PropTypes.string),
  formTitle: PropTypes.string,
  formButtonText: PropTypes.string,
  checkboxText: PropTypes.string
}

HeroSection.defaultProps = {
  title: null,
  titleFontSize: null,
  titleMobileFontSize: null,
  titleWidth: null,
  subtitle: null,
  heroBullets: null,
  formTitle: null,
  formButtonText: null,
  checkboxText: null
}

export default HeroSection
